import { addDays, addMonths } from 'date-fns';
import type {
  ActivityForm,
  ActivityPlan,
  ExternalBooking,
  ValidityPeriod
} from '@linktivity/ars-neo';
import {
  TargetDateMode,
  ValidityPeriodType,
  ValidityPeriodUnit
} from '@linktivity/ars-neo';
import { formatDateTime } from './date';

export function isOpenDatePlan(plan?: ActivityPlan): boolean {
  return plan?.targetDateMode === TargetDateMode.TargetDateModeOpenDate;
}

/**
 * The date a *session's* participants attend on — '' when there is none and a
 * validity period is shown instead.
 *
 * A session's `activityForm.targetDate` is filled in for every plan, open-date
 * included: the app picks a date to hold allotment with, which nobody attends
 * on. So only `participationDate` answers this, and `plan` is what lets a
 * session written before that field existed fall back to its target date.
 * Without a plan no fallback is taken, so such a session shows no date rather
 * than the app's own pick.
 *
 * A booking is read by {@link resolveBookingParticipationDate} instead — it has
 * a better field, and needs no plan.
 */
export function resolveParticipationDate(
  activityForm?: ActivityForm,
  plan?: ActivityPlan
): string {
  if (activityForm?.participationDate) return activityForm.participationDate;
  if (!plan || isOpenDatePlan(plan)) return '';
  return activityForm?.targetDate || '';
}

/**
 * The same date for a booking, which answers for itself with no plan to consult.
 *
 * `externalBooking.targetDate` is the date the booking was actually placed for,
 * and the backend leaves it empty for an open-date booking — unlike the
 * `targetDate` inside the form, which carries the app's own pick in both cases
 * and is what made open-date orders read as dated ones.
 *
 * So it stands in wherever `participationDate` is missing, which is every
 * booking placed before that field existed, whatever its plan. Nothing here
 * needs the product, which is what lets the order list — a year of orders, one
 * product each — read every row correctly without fetching any of them.
 */
export function resolveBookingParticipationDate(
  externalBooking?: ExternalBooking
): string {
  return (
    externalBooking?.form?.activityForm?.participationDate ||
    externalBooking?.targetDate ||
    ''
  );
}

/**
 * Resolve a validity period to a concrete expiration date (YYYY-MM-DD).
 * Returns '' when no concrete date can be derived (e.g. description-only
 * periods). Use this when the result feeds date math.
 */
export function resolveValidityExpirationDate(
  validityPeriod?: ValidityPeriod,
  baseDate?: Date
): string {
  if (!validityPeriod) return '';

  const { type, quantity, unit, fixedDate } = validityPeriod;

  switch (type) {
    case ValidityPeriodType.FromPurchaseDate:
    case ValidityPeriodType.FromActivityDate: {
      if (!quantity || !unit) return '';
      const effectiveBaseDate = baseDate ?? new Date();
      const expiry =
        unit === ValidityPeriodUnit.Month
          ? addDays(addMonths(effectiveBaseDate, quantity), -1)
          : addDays(effectiveBaseDate, quantity - 1); // Subtract 1 day to make the validity inclusive of the purchase/activity date, same as gds date
      return formatDateTime(expiry, 'YYYY-MM-DD');
    }
    case ValidityPeriodType.FixedDate: {
      const { year, month, day } = fixedDate ?? {};
      if (!year || !month || !day) return '';
      return formatDateTime(new Date(year, month - 1, day), 'YYYY-MM-DD');
    }
    default:
      return '';
  }
}

/**
 * Validity expiration for display — a concrete date when derivable, otherwise
 * the period's description.
 */
export function computeValidityExpiration(
  validityPeriod?: ValidityPeriod,
  baseDate?: Date
): string {
  return (
    resolveValidityExpirationDate(validityPeriod, baseDate) ||
    validityPeriod?.description ||
    ''
  );
}
